import React, { useCallback, useEffect, useState } from 'react';
import { gql, useSubscription } from '@apollo/client';
import { toast } from 'react-toastify';
import { BulkDownloadDocumentsJobContext } from './context';
import { useResettableMutation } from '../../hooks/useResettableMutation';
import { useHistory } from 'react-router-dom';
import { useCurrentUser } from '../../shared/currentUser';
import { ToastContent } from '../../components/react-toastify';

const START_JOB = gql`
  mutation startBulkDownloadDocumentsJob(
    $activeOn: Date!
    $includeStakeholders: Boolean
    $clientId: ID
    $policyId: ID
    $insuredId: ID
    $documentRelationshipIds: [ID]!
  ) {
    bulkDownloadDocuments(
      activeOn: $activeOn
      includeStakeholders: $includeStakeholders
      clientId: $clientId
      policyId: $policyId
      insuredId: $insuredId
      documentRelationshipIds: $documentRelationshipIds
    )
  }
`;

const ON_BULK_DOWNLOAD_DOCUMENTS_JOB_PROGRESS = gql`
  subscription jobProgress($jobId: ID!) {
    job: bulkDownloadDocumentsJobProgress(jobId: $jobId) {
      jobId
      message
      finished
      filename
      url
      errors
    }
  }
`;

// New async flow: enqueue mutation (for bulk downloads from policy/client/product pages)
const ENQUEUE_BULK_DOWNLOAD = gql`
  mutation enqueueBulkDownloadDocuments(
    $activeOn: Date!
    $includeStakeholders: Boolean
    $clientId: ID
    $policyId: ID
    $insuredId: ID
    $documentRelationshipIds: [ID]!
    $emailAddress: String!
  ) {
    enqueueBulkDownloadDocuments(
      activeOn: $activeOn
      includeStakeholders: $includeStakeholders
      clientId: $clientId
      policyId: $policyId
      insuredId: $insuredId
      documentRelationshipIds: $documentRelationshipIds
      emailAddress: $emailAddress
    )
  }
`;

export default function BulkDownloadDocumentsJobProvider({ children }) {
  const history = useHistory();
  const currentUser = useCurrentUser();

  // ── Old synchronous flow state ──
  const [jobId, setJobId] = useState(null);
  const [jobErrors, setJobErrors] = useState([]);
  const [finished, setJobFinished] = useState(true);
  const [percentComplete, setPercentComplete] = useState(0);
  const [message, setMessage] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [policyId, setPolicyId] = useState(null);
  const [insuredId, setInsuredId] = useState(null);
  const [filename, setFilename] = useState(null);
  const [url, setUrl] = useState(null);

  const clearJobId = useCallback(() => {
    setJobId(null);
    setJobErrors([]);
  }, []);

  useEffect(() => {
    if (!!jobId && !!finished) {
      setTimeout(() => clearJobId(), 10000);
    }
  }, [finished, clearJobId, jobId]);

  const [startBulkDownloadJob, { loading: syncLoading, error: syncError, reset }] = useResettableMutation(START_JOB, {
    onCompleted: data => {
      setJobFinished(false);
      setJobId(data?.bulkDownloadDocuments);
      history.push(redirectUrl);
    },
    onError: () => setTimeout(() => reset(), 10000)
  });

  const startBulkDownloadDocumentsJob = ({
    activeOn,
    includeStakeholders,
    clientId,
    policyId,
    insuredId,
    documentRelationshipIds,
    redirectUrl
  }) => {
    setJobFinished(false);
    setRedirectUrl(redirectUrl);
    setClientId(clientId);
    setPolicyId(policyId);
    setInsuredId(insuredId);
    startBulkDownloadJob({
      variables: { activeOn, includeStakeholders, clientId, policyId, insuredId, documentRelationshipIds }
    });
  };

  // Old synchronous subscription
  useSubscription(ON_BULK_DOWNLOAD_DOCUMENTS_JOB_PROGRESS, {
    skip: !jobId,
    variables: { jobId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const job = data?.job;
      const { message, finished, filename, url, progress, errors } = job;

      errors && setJobErrors(errors);
      setMessage(message);
      setJobFinished(finished);
      setPercentComplete(progress);
      setFilename(filename);
      setUrl(url);
    }
  });

  // ── New async flow ──
  const [enqueueJob, { loading: asyncLoading, error: asyncError, reset: asyncReset }] = useResettableMutation(
    ENQUEUE_BULK_DOWNLOAD,
    {
      onCompleted: () => {
        toast(
          <ToastContent
            title="Documents Queued"
            titleProps={{ className: 'text-success' }}
            icon={['fas', 'check-circle']}
            message={`Your documents are being prepared. You'll receive an email at ${currentUser?.email} when they're ready. This may take some time for large selections.`}
          />,
          { autoClose: 8000 }
        );
      },
      onError: () => setTimeout(() => asyncReset(), 10000)
    }
  );

  const enqueueBulkDownloadDocuments = useCallback(
    ({ activeOn, includeStakeholders, clientId, policyId, insuredId, documentRelationshipIds, redirectUrl }) => {
      enqueueJob({
        variables: {
          activeOn,
          includeStakeholders,
          clientId,
          policyId,
          insuredId,
          documentRelationshipIds,
          emailAddress: currentUser?.email
        }
      });

      if (redirectUrl) {
        history.push(redirectUrl);
      }
    },
    [enqueueJob, currentUser, history]
  );

  return (
    <BulkDownloadDocumentsJobContext.Provider
      value={{
        // Old synchronous flow (single doc + stakeholders)
        startBulkDownloadDocumentsJob,
        jobId,
        clientId,
        policyId,
        insuredId,
        clearJobId,
        message,
        finished,
        filename,
        url,
        progress: percentComplete,
        jobErrors,

        // New async flow (bulk downloads from policy/client/product pages)
        enqueueBulkDownloadDocuments,

        // Shared
        loading: syncLoading || asyncLoading,
        error: syncError || asyncError
      }}
    >
      {children}
    </BulkDownloadDocumentsJobContext.Provider>
  );
}
